import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { buildGroupListFromAcl, fetchPageAcl, listPagesFromAcl } from "@/lib/acl";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput, TextareaInput } from "@/components/ui/Field";
import { ChipMultiSelect } from "@/components/ui/ChipMultiSelect";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { formatDateTime } from "@/lib/format";

interface News {
  id: number;
  title: string;
  body: string;
  created_at: string;
  created_by?: string | null;
  created_by_name?: string | null;
  pages?: string[] | null;
  groups?: string[] | null;
  is_read?: boolean;
}

export function NewsSection() {
  const [items, setItems] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [pages, setPages] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    body: "",
    pages: [] as string[],
    groups: [] as string[],
  });

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await api.get<News[]>("/news");
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Konnte News nicht laden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    fetchPageAcl().then((acl) => {
      setPages(listPagesFromAcl(acl));
      setGroups(buildGroupListFromAcl(acl));
    });
  }, [reload]);

  function openModal() {
    setForm({ title: "", body: "", pages: [], groups: [] });
    setOpen(true);
  }

  async function handlePublish(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.title.trim() || !form.body.trim()) return;
    setSubmitting(true);
    try {
      await api.post<News>("/news", {
        title: form.title.trim(),
        body: form.body.trim(),
        pages: form.pages.length ? form.pages : null,
        groups: form.groups.length ? form.groups : null,
      });
      setOpen(false);
      await reload();
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Veröffentlichen fehlgeschlagen.";
      setError(message);
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(news: News) {
    if (!window.confirm(`„${news.title}“ wirklich löschen?`)) return;
    try {
      await api.del(`/news/${news.id}`);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen.");
    }
  }

  const sorted = useMemo(
    () => [...items].sort((a, b) => (a.created_at < b.created_at ? 1 : -1)),
    [items],
  );

  return (
    <Card
      title="Neuigkeiten"
      description="Aktuelle Meldungen für die App-Nutzer veröffentlichen."
      actions={<Button onClick={openModal}>Veröffentlichen</Button>}
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Spinner /> <span>Lädt …</span>
        </div>
      )}

      {!loading && error && (
        <Alert variant="error" title="Fehler">
          {error}
        </Alert>
      )}

      {!loading && !error && sorted.length === 0 && (
        <p className="text-sm text-slate-500 dark:text-slate-400">Noch keine Meldungen.</p>
      )}

      {!loading && !error && sorted.length > 0 && (
        <ul className="divide-y divide-slate-200 dark:divide-slate-800">
          {sorted.map((n) => (
            <li key={n.id} className="flex items-start justify-between gap-3 py-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{n.title}</h3>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-slate-600 dark:text-slate-300">
                  {n.body}
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  {formatDateTime(n.created_at)} · {n.created_by_name || n.created_by || "—"}
                  {n.pages?.length ? ` · Seiten: ${n.pages.join(", ")}` : ""}
                  {n.groups?.length ? ` · Gruppen: ${n.groups.join(", ")}` : ""}
                </p>
              </div>
              <Button size="sm" variant="danger" onClick={() => handleDelete(n)}>
                Löschen
              </Button>
            </li>
          ))}
        </ul>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        dirty={form.title.trim() !== "" || form.body.trim() !== ""}
        title="Neuigkeit veröffentlichen"
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button form="news-publish-form" type="submit" loading={submitting}>
              Veröffentlichen
            </Button>
          </>
        }
      >
        <form id="news-publish-form" onSubmit={handlePublish} className="space-y-4">
          <TextInput
            id="news-title"
            label="Titel"
            value={form.title}
            onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))}
            required
            autoComplete="off"
          />
          <TextareaInput
            id="news-body"
            label="Text"
            rows={6}
            value={form.body}
            onChange={(e) => setForm((s) => ({ ...s, body: e.target.value }))}
            required
          />
          <ChipMultiSelect
            id="news-pages"
            label="Auf welchen Seiten sichtbar?"
            hint="Leer lassen ⇒ keine Seitenbindung."
            options={pages}
            value={form.pages}
            placeholder="Seite(n) suchen …"
            onChange={(next) => setForm((s) => ({ ...s, pages: next }))}
          />
          <ChipMultiSelect
            id="news-groups"
            label="Für welche Gruppen sichtbar?"
            hint="Leer lassen ⇒ keine Gruppenbindung."
            options={groups}
            value={form.groups}
            placeholder="Gruppe(n) suchen …"
            onChange={(next) => setForm((s) => ({ ...s, groups: next }))}
          />
        </form>
      </Dialog>
    </Card>
  );
}
